import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Square, Copy, Check } from 'lucide-react';
import { API, getUserPhone } from '../lib/api';

const waText = (t, source) =>
  `https://wa.me/?text=${encodeURIComponent(t.replace(/\*\*(.+?)\*\*/g, '*$1*').replace(/^#+\s*/gm, '').trim() + `\n\n— via ${source} · Gynovate AI/SI`)}`;

export default function SICoachChat({ coach, source, accentGrad, accentText, chipClass, suggestions, placeholder, mdComponents, planExport }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [running, setRunning] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const abortRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || running) return;
    setInput('');
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: 'user', content: q }, { role: 'assistant', content: '' }]);
    setRunning(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(`${API}/api/si-coach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coach, message: q, phone: getUserPhone() || undefined, history }),
        signal: ctrl.signal,
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const d = line.slice(6).trim();
          if (d === '[DONE]') continue;
          try {
            const p = JSON.parse(d);
            if (p.type === 'chunk') {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + p.content };
                return copy;
              });
            }
          } catch { /* partial */ }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: 'assistant', content: 'Something went wrong — please try again.' };
          return copy;
        });
      }
    }
    setRunning(false);
  };

  const copyMsg = (text, i) => {
    navigator.clipboard.writeText(text.replace(/\*\*/g, ''));
    setCopiedIdx(i);
    setTimeout(() => setCopiedIdx(null), 1500);
  };

  const exportPlan = () => {
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const mdToHtml = (t) =>
      esc(t)
        .replace(/^### (.*)$/gm, '<h3>$1</h3>')
        .replace(/^## (.*)$/gm, '<h2>$1</h2>')
        .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
        .replace(/^[-•] (.*)$/gm, '<li>$1</li>')
        .replace(/\n{2,}/g, '<br/><br/>')
        .replace(/\n/g, '<br/>');
    const body = messages
      .map((m) => (m.role === 'user'
        ? `<p style="color:#0e7490;font-weight:700;margin-top:22px;font-size:15px">❯ ${esc(m.content)}</p>`
        : `<div style="margin-top:6px">${mdToHtml(m.content)}</div>`))
      .join('');
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>${planExport.title}</title>
      <style>body{font-family:Georgia,'Times New Roman',serif;max-width:720px;margin:28px auto;padding:0 24px;color:#1e293b;line-height:1.65;font-size:14px}h1{color:#0e7490;font-size:26px;margin-bottom:4px}h2,h3{color:#155e75}li{margin:4px 0}@media print{body{margin:8px auto}}</style>
      </head><body>
      <h1>${planExport.title}</h1>
      <p style="color:#64748b;font-size:12px">Generated by Gynovate AI/SI · ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
      <hr style="border:none;border-top:2px solid #0e7490;margin:14px 0"/>
      ${body}
      <hr style="border:none;border-top:1px solid #e2e8f0;margin-top:26px"/>
      <p style="font-size:11px;color:#94a3b8">${planExport.footer || 'Imaginative scenarios & future-proofing guidance — not predictions or medical advice.'}</p>
      </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  return (
    <div data-testid={`si-coach-${coach}`}>
      {messages.length === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6">
          {suggestions.map((s) => (
            <button
              key={s.q}
              onClick={() => send(s.q)}
              data-testid={`coach-chip-${suggestions.indexOf(s)}`}
              className={`text-left rounded-2xl px-4 py-3.5 border transition-colors ${chipClass}`}
            >
              <span className="block text-sm font-bold text-white">{s.icon} {s.label}</span>
              <span className="block text-[11px] text-white/50 mt-0.5">{s.q}</span>
            </button>
          ))}
        </div>
      )}

      <div className="space-y-4 mb-6">
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            {m.role === 'user' ? (
              <div className="max-w-[85%] px-4 py-3 rounded-3xl rounded-br-lg text-white text-sm font-medium" style={{ background: accentGrad }} data-testid="si-user-msg">{m.content}</div>
            ) : (
              <div className="max-w-[95%] w-full px-5 py-4 rounded-3xl rounded-bl-lg border border-white/10" style={{ background: 'rgba(255,255,255,0.05)' }} data-testid="si-ai-msg">
                {m.content ? (
                  <>
                    <div className="text-[15px] leading-[1.75]">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{m.content}</ReactMarkdown>
                    </div>
                    {(!running || i < messages.length - 1) && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-white/10">
                        <button onClick={() => copyMsg(m.content, i)} data-testid={`si-copy-${i}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/15 text-white/70 hover:bg-white/10 transition-colors">
                          {copiedIdx === i ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copiedIdx === i ? 'Copied' : 'Copy'}
                        </button>
                        <a href={waText(m.content, source)} target="_blank" rel="noopener noreferrer" data-testid={`si-whatsapp-${i}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-emerald-400/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors">
                          ⇪ WhatsApp
                        </a>
                      </div>
                    )}
                  </>
                ) : <p className={`text-sm animate-pulse ${accentText}`}>Thinking…</p>}
              </div>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {planExport && !running && messages.some((m) => m.role === 'assistant' && m.content) && (
        <div className="flex justify-center mb-4">
          <button
            onClick={exportPlan}
            data-testid="export-plan-btn"
            className="px-6 py-3 rounded-full text-sm font-black text-white shadow-xl hover:opacity-90 active:scale-[0.98] transition-transform flex items-center gap-2"
            style={{ background: accentGrad }}
          >
            📄 {planExport.buttonLabel || `Download "${planExport.title}" (PDF)`}
          </button>
        </div>
      )}

      <div className="sticky bottom-4 rounded-full border border-white/15 shadow-2xl p-1.5 flex gap-2 backdrop-blur-xl" style={{ background: 'rgba(15,15,25,0.85)' }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder={placeholder}
          data-testid="si-coach-input"
          className="flex-1 px-4 py-2.5 rounded-full bg-transparent text-sm text-white placeholder-white/35 focus:outline-none"
        />
        {running ? (
          <button onClick={() => abortRef.current?.abort()} data-testid="si-coach-stop" className="px-5 py-2.5 rounded-full bg-red-500/80 text-white text-sm font-bold flex items-center gap-1.5"><Square className="w-3.5 h-3.5" /> Stop</button>
        ) : (
          <button onClick={() => send()} data-testid="si-coach-send" className="px-5 py-2.5 rounded-full text-white text-sm font-bold flex items-center gap-1.5 hover:opacity-90 transition-opacity" style={{ background: accentGrad }}><Send className="w-3.5 h-3.5" /> Ask</button>
        )}
      </div>
    </div>
  );
}
